import { Button, Card, Flex, Skeleton, Tag, Typography } from 'antd';
import { useState } from 'react';

import { useGetBillingHistory } from '../../../api/generated/api';
import { TextTertiary } from '../../../ui/components/text';
import { formatDate } from '../../../ui/utils/format-date';
import { formatCredits, formatUsd } from '../shared/billing-format';

import { DiscountedPrice } from './DiscountedPrice';

import type { GetBillingHistoryResponseDtoItemsItem } from '../../../api/generated/model/getBillingHistoryResponseDtoItemsItem';
import type { GetBillingHistoryResponseDtoItemsItemStatus } from '../../../api/generated/model/getBillingHistoryResponseDtoItemsItemStatus';
import type { TagProps } from 'antd';

export type BillingHistoryProps = {
  organizationId: string;
  portalLoading: boolean;
  onOpenPortal: () => void;
};

const STATUS_TAG: Record<GetBillingHistoryResponseDtoItemsItemStatus, { label: string; color?: TagProps['color'] }> = {
  paid: { label: 'Paid', color: 'success' },
  unpaid: { label: 'Unpaid', color: 'error' },
  canceled: { label: 'Canceled' },
  refunded: { label: 'Refunded' },
  // A partial refund is still "Refunded": the row says what went back and what stayed paid.
  partly_refunded: { label: 'Refunded' },
  free: { label: 'Free' },
  change: { label: 'Change' },
  credits: { label: 'Credits' },
  scheduled: { label: 'Scheduled', color: 'warning' },
};

/** Rows shown before "Show all": the scheduled charge and the newest four. */
const COLLAPSED_ROWS = 5;

/** What the row says under its title: the date, the server's detail, and what a refund gave back. */
const rowDetail = (item: GetBillingHistoryResponseDtoItemsItem): string =>
  [
    formatDate(item.date),
    item.detail,
    item.refundedCents !== null && item.refundedCents > 0 ? `${formatUsd(item.refundedCents)} refunded` : null,
    item.status === 'canceled' ? 'no charge' : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Billing history: every Stripe invoice of the customer (paid, unpaid, canceled, refunded) with
 * the credits it bought, the trial and support credits, and the scheduled next charge. The rows
 * come ordered from the API; the card shows the first five and "Show all" expands the rest of
 * what was loaded, without another request. Receipts live in the Stripe portal ("See all
 * invoices"); only an unpaid row links out, to pay it.
 */
export const BillingHistory = ({ organizationId, portalLoading, onOpenPortal }: BillingHistoryProps) => {
  const { data, status } = useGetBillingHistory(organizationId);
  const history = data?.data;
  const [expanded, setExpanded] = useState(false);
  const items = history?.items ?? [];
  const visible = expanded ? items : items.slice(0, COLLAPSED_ROWS);
  const canCollapse = items.length > COLLAPSED_ROWS;

  return (
    <Card
      data-testid="billing-history"
      style={{ borderRadius: 20, overflow: 'hidden' }}
      styles={{ body: { padding: 0 } }}
    >
      {status === 'pending' && (
        <Flex vertical gap={12} style={{ padding: '14px 18px' }}>
          <Skeleton active title={false} paragraph={{ rows: 2, width: ['60%', '40%'] }} />
        </Flex>
      )}
      {status === 'error' && (
        <Flex style={{ padding: '12px 18px' }}>
          <TextTertiary style={{ fontSize: 13 }}>Billing history could not be loaded.</TextTertiary>
        </Flex>
      )}
      {history?.stripe === 'unavailable' && (
        <Flex style={{ padding: '12px 18px', borderBottom: '1px solid var(--ant-color-split)' }}>
          <TextTertiary style={{ fontSize: 13 }}>
            Stripe invoices are unavailable right now. Showing what K3X recorded.
          </TextTertiary>
        </Flex>
      )}
      {history && history.items.length === 0 && (
        <Flex style={{ padding: '12px 18px' }}>
          <TextTertiary style={{ fontSize: 13 }}>No charges yet.</TextTertiary>
        </Flex>
      )}
      {visible.map((item, index) => {
        const tag = STATUS_TAG[item.status];
        return (
          <Flex
            key={item.id}
            align="center"
            gap={14}
            style={{
              padding: '12px 18px',
              borderTop: index > 0 ? '1px solid var(--ant-color-split)' : undefined,
            }}
          >
            <Flex vertical gap={2} flex={1}>
              <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
                {item.credits !== null ? `${item.title} · ${formatCredits(item.credits)} credits` : item.title}
              </Typography.Text>
              <TextTertiary style={{ fontSize: 12 }}>{rowDetail(item)}</TextTertiary>
            </Flex>
            {item.status === 'unpaid' && item.invoiceUrl && (
              <Typography.Link href={item.invoiceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                Pay now
              </Typography.Link>
            )}
            <Tag
              variant="filled"
              color={tag.color}
              style={{
                padding: '3px 9px',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {tag.label}
            </Tag>
            {item.amountCents === null ? (
              <Typography.Text className="font-mono" style={{ minWidth: 60, textAlign: 'right', fontSize: 13 }}>
                —
              </Typography.Text>
            ) : (
              <DiscountedPrice
                cents={item.listAmountCents ?? item.amountCents}
                discountedCents={item.amountCents}
                className="font-mono"
                style={{ minWidth: 60, textAlign: 'right', fontSize: 13 }}
              />
            )}
          </Flex>
        );
      })}
      {(canCollapse || history?.hasMoreInvoices) && (
        <Flex
          justify="center"
          gap={8}
          style={{ padding: '8px 18px', borderTop: '1px solid var(--ant-color-split)' }}
        >
          {canCollapse && (
            <Button
              type="link"
              size="small"
              data-testid="billing-history-toggle"
              onClick={() => setExpanded((value) => !value)}
              style={{ fontSize: 12 }}
            >
              {expanded ? 'Show less' : `Show all (${items.length})`}
            </Button>
          )}
          {history?.hasMoreInvoices && (expanded || !canCollapse) && (
            <Button type="link" size="small" loading={portalLoading} onClick={onOpenPortal} style={{ fontSize: 12 }}>
              See all invoices
            </Button>
          )}
        </Flex>
      )}
    </Card>
  );
};
